import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// Note: Using native select instead of missing Select component
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
// Note: Using native table and div with scroll instead of missing Table and ScrollArea components
import { 
  Search, 
  Eye, 
  Calendar, 
  Building, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  ChevronLeft,
  ChevronRight,
  Filter,
  X
} from 'lucide-react';
import { statisticsApi } from '@/api/raynetClient';
import { DealerSurveyData } from '@/api/types';

interface SubmissionFilters {
  page: number;
  limit: number;
  search: string;
  representative: string;
  status: string;
  startDate: string;
  endDate: string;
}

interface SubmissionData {
  id: number;
  uuid: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string;
  representative: string;
  status: string;
  created_at: string;
  submitted_at: string;
  step_data: DealerSurveyData;
  final_submission: DealerSurveyData;
}

interface SubmissionsResponse {
  submissions: SubmissionData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export function SubmissionsTable() {

  const [filters, setFilters] = useState<SubmissionFilters>({
    page: 1,
    limit: 25,
    search: '',
    representative: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionData | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Fetch submissions with current filters
  const { data, isLoading, error } = useQuery({
    queryKey: ['submissions', filters],
    queryFn: () => statisticsApi.getSubmissions(filters),
    placeholderData: (previousData) => previousData,
  });

  const submissions = data as SubmissionsResponse;

  // Handle filter changes
  const handleFilterChange = (key: keyof SubmissionFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : (typeof value === 'number' ? value : parseInt(value) || 1) // Reset to page 1 when changing filters
    }));
  };

  const resetFilters = () => {
    setFilters({
      page: 1,
      limit: 25,
      search: '',
      representative: '',
      status: '',
      startDate: '',
      endDate: ''
    });
  };

  // Helper functions
  const translateStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'draft': 'Koncept',
      'submitted': 'Odeslaný',
      'synced': 'Synchronizován',
      'error': 'Chyba'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'draft': 'bg-gray-500',
      'submitted': 'bg-orange-500',
      'synced': 'bg-green-500',
      'error': 'bg-red-500'
    };
    return colorMap[status] || 'bg-gray-500';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Neuvedeno';
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const translateValue = (value: string | undefined, type: 'designImpression' | 'cooperationInterest' | 'pricePerception' | 'regionPotential') => {
    if (!value) return 'Neuvedeno';
    
    const translations: { [key: string]: { [key: string]: string } } = {
      designImpression: {
        'very-positive': 'Velmi pozitivní',
        'rather-positive': 'Spíše pozitivní',
        'neutral': 'Neutrální',
        'rather-negative': 'Spíše negativní',
        'very-negative': 'Velmi negativní'
      },
      cooperationInterest: {
        'very-interested': 'Velmi zajímá',
        'interested': 'Zajímá',
        'neutral': 'Neutrální',
        'not-interested': 'Nezajímá',
        'not-applicable': 'Nevztahuje se'
      },
      pricePerception: {
        'very-expensive': 'Velmi drahé',
        'expensive': 'Drahé',
        'reasonable': 'Přiměřené',
        'affordable': 'Dostupné',
        'very-affordable': 'Velmi dostupné'
      },
      regionPotential: {
        'very-high': 'Velmi vysoký',
        'high': 'Vysoký',
        'medium': 'Střední',
        'low': 'Nízký',
        'very-low': 'Velmi nízký'
      }
    };
    
    return translations[type]?.[value] || value;
  };

  const ViewSubmissionDetail = ({ submission }: { submission: SubmissionData }) => {
    const data = submission.final_submission || submission.step_data;
    
    return (
      <div className="h-[80vh] w-full pr-4 overflow-y-auto">
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Základní informace</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{data?.companyName || 'Neuvedeno'}</div>
                  <div className="text-sm text-muted-foreground">Firma</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{data?.contactName || 'Neuvedeno'}</div>
                  <div className="text-sm text-muted-foreground">Kontaktní osoba</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{data?.contactEmail || 'Neuvedeno'}</div>
                  <div className="text-sm text-muted-foreground">Email</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{data?.contactPhone || 'Neuvedeno'}</div>
                  <div className="text-sm text-muted-foreground">Telefon</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">
                    {data?.showroomStreet && data?.showroomCity 
                      ? `${data.showroomStreet}, ${data.showroomCity}`
                      : 'Neuvedeno'
                    }
                  </div>
                  <div className="text-sm text-muted-foreground">Adresa showroomu</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">
                    {data?.visitDate ? new Date(data.visitDate).toLocaleDateString('cs-CZ') : 'Neuvedeno'}
                  </div>
                  <div className="text-sm text-muted-foreground">Datum návštěvy</div>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Market Insights */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Tržní poznatky</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-medium">Dojem z designu</div>
                <div className="text-sm text-muted-foreground">
                  {translateValue(data?.designImpression, 'designImpression')}
                </div>
              </div>
              <div>
                <div className="font-medium">Zájem o spolupráci</div>
                <div className="text-sm text-muted-foreground">
                  {translateValue(data?.cooperationInterest, 'cooperationInterest')}
                </div>
              </div>
              <div>
                <div className="font-medium">Vnímání ceny</div>
                <div className="text-sm text-muted-foreground">
                  {translateValue(data?.pricePerception, 'pricePerception')}
                </div>
              </div>
              <div>
                <div className="font-medium">Regionální potenciál</div>
                <div className="text-sm text-muted-foreground">
                  {translateValue(data?.regionPotential, 'regionPotential')}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Competitors */}
          {data?.competitors && data.competitors.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Konkurence</h3>
              <div className="flex flex-wrap gap-2">
                {data.competitors.map((competitor, index) => (
                  <Badge key={index} variant="secondary">
                    {competitor}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Expected Support */}
          {data?.expectedSupport && data.expectedSupport.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Očekávaná podpora</h3>
              <div className="flex flex-wrap gap-2">
                {data.expectedSupport.map((support, index) => (
                  <Badge key={index} variant="outline">
                    {support}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Sales Method */}
          {data?.salesMethod && data.salesMethod.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Způsob prodeje</h3>
              <div className="flex flex-wrap gap-2">
                {data.salesMethod.map((method, index) => (
                  <Badge key={index} variant="outline">
                    {method}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Sales Potential */}
          {(data?.totalStovesPerYear || data?.designStovesPerYear || data?.typicalCustomer) && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Prodejní potenciál</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {data?.totalStovesPerYear && (
                  <div>
                    <div className="font-medium">Celkový objem kamen/rok</div>
                    <div className="text-sm text-muted-foreground">{data.totalStovesPerYear}</div>
                  </div>
                )}
                {data?.designStovesPerYear && (
                  <div>
                    <div className="font-medium">Designová kamna/rok</div>
                    <div className="text-sm text-muted-foreground">{data.designStovesPerYear}</div>
                  </div>
                )}
                {data?.typicalCustomer && (
                  <div>
                    <div className="font-medium">Typický zákazník</div>
                    <div className="text-sm text-muted-foreground">{data.typicalCustomer}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Comments */}
          {data?.comments && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Komentáře</h3>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">{data.comments}</p>
              </div>
            </div>
          )}

          {/* System Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Systémové informace</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="font-medium">Stav</div>
                <div className="text-sm text-muted-foreground">
                  <Badge className={getStatusColor(submission.status)}>
                    {translateStatus(submission.status)}
                  </Badge>
                </div>
              </div>
              <div>
                <div className="font-medium">Obchodník</div>
                <div className="text-sm text-muted-foreground">{submission.representative}</div>
              </div>
              <div>
                <div className="font-medium">Vytvořeno</div>
                <div className="text-sm text-muted-foreground">{formatDate(submission.created_at)}</div>
              </div>
              <div>
                <div className="font-medium">Odesláno</div>
                <div className="text-sm text-muted-foreground">{formatDate(submission.submitted_at)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">Chyba při načítání formulářů: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtry a vyhledávání
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div>
              <Label htmlFor="search">Vyhledávání</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Firma, kontakt, email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="representative">Obchodník</Label>
              <select
                id="representative"
                value={filters.representative}
                onChange={(e) => handleFilterChange('representative', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Všichni</option>
                {/* Add representatives dynamically */}
              </select>
            </div>
            <div>
              <Label htmlFor="status">Stav</Label>
              <select
                id="status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Všechny</option>
                <option value="draft">Koncept</option>
                <option value="submitted">Odeslaný</option>
                <option value="synced">Synchronizován</option>
                <option value="error">Chyba</option>
              </select>
            </div>
            <div>
              <Label htmlFor="startDate">Od data</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="endDate">Do data</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={resetFilters} className="w-full">
                <X className="h-4 w-4 mr-2" />
                Vymazat
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle>
            Formuláře ({submissions?.pagination?.total || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <table className="w-full border-collapse">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Firma</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Kontakt</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Obchodník</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Stav</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Vytvořeno</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Akce</th>
                </tr>
              </thead>
              <tbody>
                {submissions?.submissions?.map((submission) => (
                  <tr key={submission.id} className="border-b hover:bg-muted/50">
                    <td className="p-4 align-middle">
                      <div>
                        <div className="font-medium">{submission.company_name}</div>
                        <div className="text-sm text-muted-foreground">{submission.email}</div>
                      </div>
                    </td>
                    <td className="p-4 align-middle">
                      <div>
                        <div className="font-medium">{submission.contact_person}</div>
                        <div className="text-sm text-muted-foreground">{submission.phone}</div>
                      </div>
                    </td>
                    <td className="p-4 align-middle">{submission.representative}</td>
                    <td className="p-4 align-middle">
                      <Badge className={getStatusColor(submission.status)}>
                        {translateStatus(submission.status)}
                      </Badge>
                    </td>
                    <td className="p-4 align-middle">{formatDate(submission.created_at)}</td>
                    <td className="p-4 align-middle">
                      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedSubmission(submission)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Detail
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl">
                          <DialogHeader>
                            <DialogTitle>
                              Detail formuláře - {selectedSubmission?.company_name}
                            </DialogTitle>
                          </DialogHeader>
                          {selectedSubmission && (
                            <ViewSubmissionDetail submission={selectedSubmission} />
                          )}
                        </DialogContent>
                      </Dialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {submissions?.pagination && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                Stránka {submissions.pagination.page} z {submissions.pagination.pages}
                ({submissions.pagination.total} celkem)
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                  disabled={submissions.pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Předchozí
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('page', Math.min(submissions.pagination.pages, filters.page + 1))}
                  disabled={submissions.pagination.page >= submissions.pagination.pages}
                >
                  Následující
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}